import { useParams } from "react-router";
import { AirMicrobialReferenceField } from "@/services/backend/samples/components/air-microbial-reference-field";
import {
  Position,
  positionFieldName,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { useEffect, useState } from "react";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { RemoveAssessmentDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/remove-assessment-dialog";
import { ComponentData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { AlertCircle, Plus, PlusCircle, RefreshCw, Search } from "lucide-react";
import { H4 } from "@/shared/components/typography";
import { v4 } from "uuid";
import { MicrobialAssessment } from "@/services/backend/htz/inspection/types/microbial-assessment";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Kind } from "@/services/backend/samples/types/kind";
import { Separator } from "@/shared/components/ui/separator";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { EntityId } from "@/shared/types/entity-id";
import {
  activateAirMicrobialSetSample,
  AirMicrobialSample,
  airMicrobialSampleIsActivated,
  AirMicrobialSampleSet,
  deactivateAirMicrobialSetSample,
  updateAirMicrobialSetCaso,
  updateAirMicrobialSetComment,
  updateAirMicrobialSetDg18,
  updateAirMicrobialSetLab,
  updateAirMicrobialSetMalt,
  updateAirMicrobialSetNumber,
  updateCreatedAtInAirSet,
  updateDeviceIdInSet,
  updateSamplingVolumeInSet,
} from "@/services/backend/samples/types/air-microbial-sample";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { GrowthMedium } from "@/services/backend/samples/types/growth-medium";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { CheckedState } from "@radix-ui/react-checkbox";
import {
  generateSampleNumber,
  Sample,
  updateLocation,
  updateSampleComment,
  updateSampleCreatedAt,
  updateSampleLab,
  updateSampleNumber,
  updateSampleTakers,
  updateSpot,
} from "@/services/backend/samples/types/sample";
import { TextareaDebounced } from "@/shared/components/inputs/textarea-debounced";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { DateStringPicker } from "@/shared/components/date-picker";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  activateSurfaceMicrobialSetSample,
  deactivateSurfaceMicrobialSetSample,
  SurfaceMicrobialSample,
  surfaceMicrobialSampleIsActivated,
  SurfaceMicrobialSampleSet,
  updateCreatedAtInSurfaceSet,
  updateSurfaceMicrobialSetCaso,
  updateSurfaceMicrobialSetComment,
  updateSurfaceMicrobialSetDg18,
  updateSurfaceMicrobialSetLab,
  updateSurfaceMicrobialSetMalt,
  updateSurfaceMicrobialSetNumber,
} from "@/services/backend/samples/types/surface-microbial-sample";
import { DustDensitySample } from "@/services/backend/samples/types/dust-density-sample";
import {
  updateTemperature,
  WaterKind,
  WaterSample,
} from "@/services/backend/samples/types/water-sample";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  updateAirMicrobialSampleSetCache,
  useSamplesAirMicrobialSampleSetListQuery,
  useSamplesAirMicrobialSampleSetPutMutation,
  useSamplesAirMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/air-microbial-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  updateSurfaceMicrobialSampleSetCache,
  useSamplesSurfaceMicrobialSampleSetPutMutation,
  useSamplesSurfaceMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/surface-microbial-api";
import {
  updateDustDensitySampleCache,
  useSamplesDustDensitySamplePutMutation,
  useSamplesDustDensitySampleShowQuery,
} from "@/services/backend/samples/api/dust-density-sample-api";
import {
  updateWaterSampleCache,
  useSamplesWaterSamplePutMutation,
  useSamplesWaterSampleShowQuery,
} from "@/services/backend/samples/api/water-sample-api";
import { useSamplesLaboratoryListQuery } from "@/services/backend/samples/api/lab-api";
import { useSamplesDeviceListQuery } from "@/services/backend/samples/api/device-api";
import { FilterMode } from "@/shared/lib/filter/filter";
import {
  countFieldErrors,
  useErrValidationFailedContext,
  ErrValidationFailedContextProvider,
} from "@/shared/lib/errors/err-validation-failed-context";
import { TextInputField } from "@/shared/components/field/input-field";
import { BooleanSelect } from "@/shared/components/form/boolean-select";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import {
  useHtzWorkOrderAddAssessmentMutation,
  useHtzWorkOrderAddMicrobialAssessmentWithSampleMutation,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { TableCard } from "@/shared/components/tables/table-card";

export function MicrobialInspection({
  workOrder,
  position,
  activeComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
}) {
  const [assessmentId, setAssessmentId] = useState<string | null>(null);

  return (
    <ScrollArea className="h-[65vh] space-y-2">
      <MicrobialAssessmentTable
        workOrder={workOrder}
        position={position}
        activeComponent={activeComponent}
        selectedAssessmentId={assessmentId}
        onAssessmentSelectedId={(a) => setAssessmentId(a)}
      />
      <div className="p-2 py-4">
        {position.microbialAssessments
          .filter((a) => a.componentId === activeComponent?.componentId)
          .map((assessment) => {
            if (assessmentId !== assessment.id) return null;
            return (
              <MicrobialAssessmentForm
                key={assessment.id}
                workOrder={workOrder}
                assessment={assessment}
              />
            );
          })}
      </div>
    </ScrollArea>
  );
}

export function AddMicrobialAssessmentButton({
  workOrder,
  position,
  activeComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
}) {
  const [
    addWithSample,
    { isLoading: addWithSampleIsLoading, error: addWithSampleError, reset },
  ] = useHtzWorkOrderAddMicrobialAssessmentWithSampleMutation();
  const [
    addAssessment,
    { isLoading: addIsLoading, error: addError, reset: addReset },
  ] = useHtzWorkOrderAddAssessmentMutation();

  const isLoading = addWithSampleIsLoading || addIsLoading;
  const error = addWithSampleError || addError;

  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
      addReset();
    }
  }, [addReset, error, reset, toast]);

  const { resetField } = useErrValidationFailedContext();
  const errorKey = positionFieldName(position, "microbialAssessments");

  const { data: airHandlingUnit } = useHtzAirHandlingUnitDataShowQuery(
    { id: position.ahuDataId ?? "" },
    { skip: !position.ahuDataId },
  );

  // The backend creates the sample with the defaults of the company's base
  // data and adds the assessment referencing it, in one transaction.
  const doAdd = (sampleKind: Kind, waterKind?: WaterKind) => () => {
    if (isLoading || !airHandlingUnit || !activeComponent) {
      return;
    }

    addWithSample({
      id: position.workOrderId,
      positionId: position.id,
      assessmentId: v4(),
      componentId: activeComponent.componentId,
      sampleId: v4(),
      sampleKind,
      waterKind,
      number: generateSampleNumber(),
      location: airHandlingUnit.designation,
      spot: activeComponent.name ?? "",
    });
    resetField(errorKey);
  };

  // An existing sample, e.g. an outdoor air sample, is referenced by a new
  // assessment.
  const doAddExisting = (sampleKind: Kind, sampleId: string) => {
    if (isLoading || !activeComponent) {
      return;
    }

    addAssessment({
      id: position.workOrderId,
      positionId: position.id,
      type: "microbial",
      assessmentId: v4(),
      componentId: activeComponent.componentId,
      sampleKind,
      sampleId,
    });
    resetField(errorKey);
  };

  const disabled =
    isImmutable(workOrder) || activeComponent === null || isLoading;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button>
          <PlusCircle />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("Neue Probe hinzufügen")}</DropdownMenuLabel>
        <DropdownMenuItem onClick={doAdd(Kind.AirMicrobial)}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Luftkeimprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.SurfaceMicrobial)}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Oberflächenkeimprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.DustDensity)}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Staubdichtemessung")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.Water, "cold")}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Kaltwasserprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.Water, "warm")}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Warmwasserprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("Bestehende Luftkeimprobe")}</DropdownMenuLabel>
        <SampleSearch workOrder={workOrder} onClick={doAddExisting} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SampleSearch({
  workOrder,
  onClick,
}: {
  workOrder: WorkOrder;
  onClick: (kind: Kind, sampleId: string) => void;
}) {
  const { data: list } = useSamplesAirMicrobialSampleSetListQuery({
    company: { active: true, values: [workOrder.companyId] },
    document: { active: true, mode: FilterMode.Or, values: [workOrder.id] },
  });
  const data = list?.sampleSets;
  const [search, setSearch] = useState("");

  if (!data) {
    return t("Keine Proben");
  }

  const samples = data.filter((s: AirMicrobialSampleSet) => {
    const lower = search.toLowerCase();
    return (
      s.number.toLowerCase().includes(lower) ||
      s.location.toLowerCase().includes(lower)
    );
  });

  return (
    <div className="grid gap-2 px-2">
      <div className="flex gap-2">
        <Input
          placeholder={t("Suchen")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button>
          <Search />
        </Button>
      </div>
      <Separator />
      <ScrollArea className="max-h-60">
        <div className="grid gap-1">
          {samples?.map((s: AirMicrobialSampleSet) => (
            <Button
              key={s.id}
              variant="ghost"
              className="justify-start font-normal has-[>svg]:px-0"
              size="sm"
              onClick={() => onClick(Kind.AirMicrobial, s.id)}
            >
              <Plus className="text-muted-foreground mr-2" />
              <span className="whitespace-nowrap">{`${s.number} - ${s.location} - ${s.spot}`}</span>
            </Button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

function MicrobialAssessmentTable({
  workOrder,
  position,
  activeComponent,
  selectedAssessmentId,
  onAssessmentSelectedId,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
  selectedAssessmentId: string | null;
  onAssessmentSelectedId: (a: string | null) => void;
}) {
  // The keys of a microbial assessment (kind, sample, component) are set
  // when it is added; there is no field to edit them.
  const { fieldErrors } = useErrValidationFailedContext();

  const toggle = (assessment: MicrobialAssessment) => {
    if (assessment.id === selectedAssessmentId) {
      onAssessmentSelectedId(null);
    } else {
      onAssessmentSelectedId(assessment.id);
    }
  };

  return (
    <TableCard className="shadow-none">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8">{t("Probenart")}</TableHead>
            <TableHead className="h-8">{t("")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {position.microbialAssessments
            ?.filter(
              (a: MicrobialAssessment) =>
                a.componentId === activeComponent?.componentId,
            )
            .map((assessment: MicrobialAssessment) => (
              <TableRow
                key={assessment.id}
                className={cn(
                  "cursor-pointer",
                  assessment.id === selectedAssessmentId ? "bg-accent" : "",
                )}
              >
                <TableCell className="py-1" onClick={() => toggle(assessment)}>
                  <span className="flex items-center space-x-2">
                    {countFieldErrors(
                      fieldErrors,
                      positionFieldName(
                        position,
                        "microbialAssessments",
                        assessment.id,
                      ),
                    ) > 0 && <AlertCircle className="h-6 w-6 text-red-500" />}
                    <span>{t(assessment.kind)}</span>
                  </span>
                </TableCell>
                <TableCell className="py-1 text-right">
                  <RemoveAssessmentDialog
                    position={position}
                    assessmentId={assessment.id}
                    title={t("Beurteilung löschen?")}
                    // Samples are not deleted, since they may be used by
                    // another position as well, typically the
                    // "Außenluftprobe" re-used across air handling units.
                    description={t(
                      "Die zugeordnete Probe wird nicht automatisch mit gelöscht und kann an anderer Stelle weiterverwendet werden.",
                    )}
                    onRemoved={() => onAssessmentSelectedId(null)}
                    disabled={isImmutable(workOrder)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function MicrobialAssessmentForm({
  workOrder,
  assessment,
}: {
  workOrder: WorkOrder;
  assessment: MicrobialAssessment;
}) {
  // The sample is an entity of its own. Its form gets a scope of its own, so
  // the keys of its validation errors ("number", "labId", ...) do not land
  // in the scope of the work order.
  return (
    <ErrValidationFailedContextProvider>
      <SampleForm workOrder={workOrder} assessment={assessment} />
    </ErrValidationFailedContextProvider>
  );
}

function SampleForm({
  workOrder,
  assessment,
}: {
  workOrder: WorkOrder;
  assessment: MicrobialAssessment;
}) {
  const immutable = isImmutable(workOrder);

  switch (assessment.kind) {
    case Kind.AirMicrobial:
      return (
        <AirMicrobialSampleFormLoader
          id={assessment.sampleId}
          immutable={immutable}
        />
      );
    case Kind.SurfaceMicrobial:
      return (
        <SurfaceMicrobialSampleFormLoader
          id={assessment.sampleId}
          immutable={immutable}
        />
      );
    case Kind.DustDensity:
      return (
        <DustDensitySampleFormLoader
          id={assessment.sampleId}
          immutable={immutable}
        />
      );
    case Kind.Water:
      return (
        <WaterSampleFormLoader id={assessment.sampleId} immutable={immutable} />
      );
    default:
      return null;
  }
}

function AirMicrobialSampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } = useSamplesAirMicrobialSampleSetShowQuery({
    id: id!,
  });

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.AirMicrobial)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <AirMicrobialSampleForm sampleSet={data!} immutable={immutable} />;
}

function AirMicrobialSampleForm({
  sampleSet,
  immutable,
}: {
  sampleSet: AirMicrobialSampleSet;
  immutable: boolean;
}) {
  const { workOrderId } = useParams();
  // The sample renders from the cache and is saved as a whole, see the
  // auto-saving convention.
  const [set, { error }] = useOptimisticDebouncedMutation(
    sampleSet,
    updateAirMicrobialSampleSetCache,
    useSamplesAirMicrobialSampleSetPutMutation(),
  );

  return (
    <div className="h-full">
      <H4>{t(Kind.AirMicrobial)}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={sampleSet.number}
            onNumberChange={(number) =>
              set(updateAirMicrobialSetNumber(sampleSet, number))
            }
            error={error}
            disabled={immutable}
          />
          <LaboratorySelect
            labId={sampleSet.labId}
            onLabIdChange={(labId) =>
              set(updateAirMicrobialSetLab(sampleSet, labId))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInputField
          label={t("Ort")}
          text={sampleSet.location}
          onTextChange={(location) => set(updateLocation(sampleSet, location))}
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInputField
          label={t("Stelle")}
          text={sampleSet.spot}
          onTextChange={(spot) => set(updateSpot(sampleSet, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <AirMicrobialReferenceField
          set={sampleSet}
          companyId={sampleSet.companyId}
          documentIds={[workOrderId!]}
          disabled={immutable}
        />
        <TextInputField
          label={t("Probenehmer")}
          text={sampleSet.sampleTakers}
          onTextChange={(sampleTakers) =>
            set(updateSampleTakers(sampleSet, sampleTakers))
          }
          errors={inputValidationErrorMessages(error, "sampleTakers")}
          disabled={immutable}
        />
        <CreatedAtInput
          createdAt={sampleSet.caso ? sampleSet.caso.createdAt : null}
          onCreatedAtChange={(createdAt) =>
            set(updateCreatedAtInAirSet(sampleSet, createdAt))
          }
          error={error}
          disabled={immutable}
        />
        <DeviceSelect
          deviceId={sampleSet.caso ? sampleSet.caso.deviceId : null}
          onDeviceIdChange={(deviceId) =>
            set(updateDeviceIdInSet(sampleSet, deviceId))
          }
          error={error}
          disabled={immutable}
        />
        <SamplingVolumeInput
          volume={sampleSet.caso ? sampleSet.caso.samplingVolume : 0}
          onVolumeChange={(volume) =>
            set(updateSamplingVolumeInSet(sampleSet, volume))
          }
          error={error}
          disabled={immutable}
        />
        <CommentInput
          comment={sampleSet.comment}
          onCommentChange={(comment) =>
            set(updateAirMicrobialSetComment(sampleSet, comment))
          }
          error={error}
          disabled={immutable}
        />
        <Tabs defaultValue={GrowthMedium.CASO}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value={GrowthMedium.CASO}>
              {t(GrowthMedium.CASO)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.MALT}>
              {t(GrowthMedium.MALT)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.DG18} className="uppercase">
              {t(GrowthMedium.DG18)}
            </TabsTrigger>
          </TabsList>
          <div className="p-2">
            <TabsContent value={GrowthMedium.CASO}>
              <AirMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.CASO}
                set={sampleSet}
                onSetChange={set}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.MALT}>
              <AirMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.MALT}
                set={sampleSet}
                onSetChange={set}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.DG18}>
              <AirMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.DG18}
                set={sampleSet}
                onSetChange={set}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function SurfaceMicrobialSampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } =
    useSamplesSurfaceMicrobialSampleSetShowQuery({
      id: id!,
    });

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.SurfaceMicrobial)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <SurfaceMicrobialSampleForm sampleSet={data!} immutable={immutable} />;
}

function SurfaceMicrobialSampleForm({
  sampleSet,
  immutable,
}: {
  sampleSet: SurfaceMicrobialSampleSet;
  immutable: boolean;
}) {
  // The sample renders from the cache and is saved as a whole, see the
  // auto-saving convention.
  const [set, { error }] = useOptimisticDebouncedMutation(
    sampleSet,
    updateSurfaceMicrobialSampleSetCache,
    useSamplesSurfaceMicrobialSampleSetPutMutation(),
  );

  return (
    <div className="h-full">
      <H4>{t(Kind.SurfaceMicrobial)}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={sampleSet.number}
            onNumberChange={(number) =>
              set(updateSurfaceMicrobialSetNumber(sampleSet, number))
            }
            error={error}
            disabled={immutable}
          />
          <LaboratorySelect
            labId={sampleSet.labId}
            onLabIdChange={(labId) =>
              set(updateSurfaceMicrobialSetLab(sampleSet, labId))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInputField
          label={t("Ort")}
          text={sampleSet.location}
          onTextChange={(location) => set(updateLocation(sampleSet, location))}
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInputField
          label={t("Stelle")}
          text={sampleSet.spot}
          onTextChange={(spot) => set(updateSpot(sampleSet, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <TextInputField
          label={t("Probenehmer")}
          text={sampleSet.sampleTakers}
          onTextChange={(sampleTakers) =>
            set(updateSampleTakers(sampleSet, sampleTakers))
          }
          errors={inputValidationErrorMessages(error, "sampleTakers")}
          disabled={immutable}
        />
        <CreatedAtInput
          createdAt={sampleSet.caso ? sampleSet.caso.createdAt : null}
          onCreatedAtChange={(createdAt) =>
            set(updateCreatedAtInSurfaceSet(sampleSet, createdAt))
          }
          error={error}
          disabled={immutable}
        />
        <CommentInput
          comment={sampleSet.comment}
          onCommentChange={(comment) =>
            set(updateSurfaceMicrobialSetComment(sampleSet, comment))
          }
          error={error}
          disabled={immutable}
        />
        <Tabs defaultValue={GrowthMedium.CASO}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value={GrowthMedium.CASO}>
              {t(GrowthMedium.CASO)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.MALT}>
              {t(GrowthMedium.MALT)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.DG18} className="uppercase">
              {t(GrowthMedium.DG18)}
            </TabsTrigger>
          </TabsList>
          <div className="p-2">
            <TabsContent value={GrowthMedium.CASO}>
              <SurfaceMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.CASO}
                set={sampleSet}
                onSetChange={set}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.MALT}>
              <SurfaceMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.MALT}
                set={sampleSet}
                onSetChange={set}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.DG18}>
              <SurfaceMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.DG18}
                set={sampleSet}
                onSetChange={set}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function DustDensitySampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } = useSamplesDustDensitySampleShowQuery({
    id: id!,
  });

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.DustDensity)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <DustDensitySampleForm sample={data!} immutable={immutable} />;
}

function DustDensitySampleForm({
  sample,
  immutable,
}: {
  sample: DustDensitySample;
  immutable: boolean;
}) {
  // The sample renders from the cache and is saved as a whole, see the
  // auto-saving convention.
  const [set, { error }] = useOptimisticDebouncedMutation(
    sample,
    updateDustDensitySampleCache,
    useSamplesDustDensitySamplePutMutation(),
  );

  return (
    <div className="h-full">
      <H4>{t(Kind.DustDensity)}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={sample.number}
            onNumberChange={(number) => set(updateSampleNumber(sample, number))}
            error={error}
            disabled={immutable}
          />
          <CreatedAtInput
            createdAt={sample.createdAt}
            onCreatedAtChange={(createdAt) =>
              set(updateSampleCreatedAt(sample, createdAt))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInputField
          label={t("Ort")}
          text={sample.location}
          onTextChange={(location) => set(updateLocation(sample, location))}
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInputField
          label={t("Stelle")}
          text={sample.spot}
          onTextChange={(spot) => set(updateSpot(sample, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <LaboratorySelect
          labId={sample.labId}
          onLabIdChange={(labId) => set(updateSampleLab(sample, labId))}
          error={error}
          disabled={immutable}
        />
        <CommentInput
          comment={sample.comment}
          onCommentChange={(comment) =>
            set(updateSampleComment(sample, comment))
          }
          error={error}
          disabled={immutable}
        />
      </div>
    </div>
  );
}

function WaterSampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } = useSamplesWaterSampleShowQuery({
    id: id!,
  });

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.Water)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <WaterSampleForm sample={data!} immutable={immutable} />;
}

function WaterSampleForm({
  sample,
  immutable,
}: {
  sample: WaterSample;
  immutable: boolean;
}) {
  // The sample renders from the cache and is saved as a whole, see the
  // auto-saving convention.
  const [set, { error }] = useOptimisticDebouncedMutation(
    sample,
    updateWaterSampleCache,
    useSamplesWaterSamplePutMutation(),
  );

  return (
    <div className="h-full">
      <H4>
        {t(Kind.Water)}
        {sample.waterKind === "warm" ? t(" - Warmwasser") : t(" - Kaltwasser")}
      </H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={sample.number}
            onNumberChange={(number) => set(updateSampleNumber(sample, number))}
            error={error}
            disabled={immutable}
          />
          <CreatedAtInput
            createdAt={sample.createdAt}
            onCreatedAtChange={(createdAt) =>
              set(updateSampleCreatedAt(sample, createdAt))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInputField
          label={t("Ort")}
          text={sample.location}
          onTextChange={(location) => set(updateLocation(sample, location))}
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInputField
          label={t("Stelle")}
          text={sample.spot}
          onTextChange={(spot) => set(updateSpot(sample, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <LaboratorySelect
          labId={sample.labId}
          onLabIdChange={(labId) => set(updateSampleLab(sample, labId))}
          error={error}
          disabled={immutable}
        />
        <div className="grid grid-cols-2 gap-4">
          <TemperatureInput
            temperature={sample.temperature}
            onTemperatureChange={(temperature) =>
              set(updateTemperature(sample, temperature))
            }
            error={error}
            disabled={immutable}
          />
          <BooleanSelect
            label={t("Probenentnahme desinfiziert")}
            field="desinfected"
            value={sample.desinfected}
            onValueChange={(desinfected) => set({ ...sample, desinfected })}
            errors={inputValidationErrorMessages(error, "desinfected")}
          />
        </div>
        <CommentInput
          comment={sample.comment}
          onCommentChange={(comment) =>
            set(updateSampleComment(sample, comment))
          }
          error={error}
          disabled={immutable}
        />
      </div>
    </div>
  );
}

function NumberInput({
  number,
  onNumberChange,
  error,
  disabled,
}: {
  number: string;
  onNumberChange: (number: string) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Probennummer")}</Label>
      <InputDebounced
        type="text"
        value={number}
        onChange={(e) => onNumberChange(e.target.value)}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="number" />
    </div>
  );
}

function TemperatureInput({
  temperature,
  onTemperatureChange,
  error,
  disabled,
}: {
  temperature: number;
  onTemperatureChange: (temperature: number) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Temperatur")}</Label>
      <InputDebounced
        type="number"
        value={temperature}
        onChange={(e) => onTemperatureChange(Number(e.target.value))}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="temperature" />
    </div>
  );
}

function LaboratorySelect({
  labId,
  onLabIdChange,
  error,
  disabled,
}: {
  labId: EntityId;
  onLabIdChange: (labId: EntityId) => void;
  error: unknown;
  disabled?: boolean;
}) {
  const {
    data: laboratoryList,
    isLoading,
    error: loadingError,
  } = useSamplesLaboratoryListQuery({});
  const laboratories = laboratoryList?.labs ?? [];

  if (isLoading) {
    return (
      <div className="grid content-start gap-1.5">
        <Label>{t("Labor")}</Label>
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (loadingError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Labor")}</Label>
      <Select
        value={String(labId)}
        onValueChange={onLabIdChange}
        disabled={disabled ?? false}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Labor auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {laboratories.map((lab) => (
            <SelectItem key={lab.id} value={lab.id}>
              {lab.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <InputValidationErrors error={error} field="labId" />
    </div>
  );
}

function AirMicrobialSampleSetSampleFields({
  growthMedium,
  set,
  onSetChange,
  error,
  disabled,
}: {
  growthMedium: GrowthMedium;
  set: AirMicrobialSampleSet;
  onSetChange: (set: AirMicrobialSampleSet) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={airMicrobialSampleIsActivated(set, growthMedium)}
          onCheckedChange={(state: CheckedState) => {
            switch (state) {
              case true: {
                onSetChange(activateAirMicrobialSetSample(set, growthMedium));
                break;
              }
              case false: {
                onSetChange(deactivateAirMicrobialSetSample(set, growthMedium));
                break;
              }
              default:
                break;
            }
          }}
          disabled={disabled}
        />
        <Label htmlFor="terms">{`Hat ${t(growthMedium)} Probe`}</Label>
      </div>
      {airMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.CASO && (
          <AirMicrobialSampleFields
            sample={set.caso!}
            onSampleChange={(sample) =>
              onSetChange(updateAirMicrobialSetCaso(set, sample))
            }
            error={error}
          />
        )}
      {airMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.MALT && (
          <AirMicrobialSampleFields
            sample={set.malt!}
            onSampleChange={(sample) =>
              onSetChange(updateAirMicrobialSetMalt(set, sample))
            }
            error={error}
          />
        )}
      {airMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.DG18 && (
          <AirMicrobialSampleFields
            sample={set.dg18!}
            onSampleChange={(sample) =>
              onSetChange(updateAirMicrobialSetDg18(set, sample))
            }
            error={error}
          />
        )}
    </div>
  );
}

function AirMicrobialSampleFields({
  sample,
  onSampleChange,
  error,
  disabled,
}: {
  sample: AirMicrobialSample;
  onSampleChange: (sample: AirMicrobialSample) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <BaseSampleFields
        sample={sample}
        onSampleChange={onSampleChange}
        error={error}
        disableSetBoundFields
        disabled={disabled}
      />
      {/*
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1">
          <SamplingVolumeInput
            volume={sample.samplingVolume}
            onVolumeChange={(volume) =>
              onSampleChange(updateSamplingVolume(sample, volume))
            }
            error={error}
            disabled={disabled}
          />
        </div>
        <div className="col-span-2">
          <DeviceSelect
            deviceId={sample.deviceId}
            onDeviceIdChange={(deviceId) =>
              onSampleChange(updateDeviceId(sample, deviceId))
            }
            error={error}
            disabled={disabled}
          />
        </div>
      </div>
      */}
    </div>
  );
}

function SamplingVolumeInput({
  volume,
  onVolumeChange,
  error,
  disabled,
}: {
  volume: number;
  onVolumeChange: (volume: number) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Ansaugvolumen")}</Label>
      <InputDebounced
        type="number"
        value={volume}
        onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
        disabled={disabled}
      />
      <InputValidationErrors error={error} field="samplingVolume" />
    </div>
  );
}

function DeviceSelect({
  deviceId,
  onDeviceIdChange,
  error,
  disabled,
}: {
  deviceId: EntityId;
  onDeviceIdChange: (deviceId: EntityId) => void;
  error: unknown;
  disabled?: boolean;
}) {
  const {
    data: deviceList,
    isLoading,
    error: loadingError,
  } = useSamplesDeviceListQuery({});
  const devices = deviceList?.devices ?? [];

  if (isLoading) {
    return (
      <div className="grid content-start gap-1.5">
        <Label>{t("Verwendetes Gerät")}</Label>
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (loadingError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Verwendetes Gerät")}</Label>
      <Select
        value={String(deviceId)}
        onValueChange={onDeviceIdChange}
        disabled={disabled ?? false}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Gerät auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {devices.map((device) => (
            <SelectItem key={device.id} value={device.id}>
              {device.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <InputValidationErrors error={error} field="deviceId" />
    </div>
  );
}

function SurfaceMicrobialSampleSetSampleFields({
  growthMedium,
  set,
  onSetChange,
  error,
  disabled,
}: {
  growthMedium: GrowthMedium;
  set: SurfaceMicrobialSampleSet;
  onSetChange: (set: SurfaceMicrobialSampleSet) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={surfaceMicrobialSampleIsActivated(set, growthMedium)}
          onCheckedChange={(state: CheckedState) => {
            switch (state) {
              case true: {
                onSetChange(
                  activateSurfaceMicrobialSetSample(set, growthMedium),
                );
                break;
              }
              case false: {
                onSetChange(
                  deactivateSurfaceMicrobialSetSample(set, growthMedium),
                );
                break;
              }
              default:
                break;
            }
          }}
          disabled={disabled}
        />
        <Label htmlFor="terms">{`Hat ${t(growthMedium)} Probe`}</Label>
      </div>
      {surfaceMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.CASO && (
          <SurfaceMicrobialSampleFields
            sample={set.caso!}
            onSampleChange={(sample) =>
              onSetChange(updateSurfaceMicrobialSetCaso(set, sample))
            }
            error={error}
            disabled={disabled}
          />
        )}
      {surfaceMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.MALT && (
          <SurfaceMicrobialSampleFields
            sample={set.malt!}
            onSampleChange={(sample) =>
              onSetChange(updateSurfaceMicrobialSetMalt(set, sample))
            }
            error={error}
            disabled={disabled}
          />
        )}
      {surfaceMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.DG18 && (
          <SurfaceMicrobialSampleFields
            sample={set.dg18!}
            onSampleChange={(sample) =>
              onSetChange(updateSurfaceMicrobialSetDg18(set, sample))
            }
            error={error}
            disabled={disabled}
          />
        )}
    </div>
  );
}

function SurfaceMicrobialSampleFields({
  sample,
  onSampleChange,
  error,
  disabled,
}: {
  sample: SurfaceMicrobialSample;
  onSampleChange: (sample: SurfaceMicrobialSample) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <BaseSampleFields
        sample={sample}
        onSampleChange={onSampleChange}
        error={error}
        disableSetBoundFields
        disabled={disabled}
      />
    </div>
  );
}

function BaseSampleFields<S extends Sample>({
  sample,
  onSampleChange,
  error,
  disableSetBoundFields,
  disabled,
}: {
  sample: S;
  onSampleChange: (sample: S) => void;
  error: unknown;
  disableSetBoundFields?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          number={sample.number}
          onNumberChange={(number) =>
            onSampleChange(updateSampleNumber(sample, number))
          }
          error={error}
          disabled={disableSetBoundFields || disabled}
        />
        <CreatedAtInput
          createdAt={sample.createdAt}
          onCreatedAtChange={(createdAt) =>
            onSampleChange(updateSampleCreatedAt(sample, createdAt))
          }
          error={error}
          disabled
        />
      </div>
      <LaboratorySelect
        labId={sample.labId}
        onLabIdChange={(labId) =>
          onSampleChange(updateSampleLab(sample, labId))
        }
        error={error}
        disabled={disableSetBoundFields || disabled}
      />
      <CommentInput
        comment={sample.comment}
        onCommentChange={(comment) =>
          onSampleChange(updateSampleComment(sample, comment))
        }
        error={error}
        disabled={disabled}
      />
    </div>
  );
}

function CommentInput({
  comment,
  onCommentChange,
  error,
  disabled,
}: {
  comment: string;
  onCommentChange: (comment: string) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Kommentar (für Labor)")}</Label>
      <TextareaDebounced
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="comment" />
    </div>
  );
}

function CreatedAtInput({
  createdAt,
  onCreatedAtChange,
  error,
  disabled,
}: {
  createdAt: string | null;
  onCreatedAtChange: (createdAt: string | null) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Erstellt am")}</Label>
      <DateStringPicker
        date={createdAt}
        onDateChange={onCreatedAtChange}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="createdAt" />
    </div>
  );
}
